import { setDetailsItem, setSelectedIds } from "@/store/slices/detailsDrawerSlice";
import { setTableChecked } from "@/store/slices/tableSlice";
import { store } from "@/store/store";
import { ModelType } from "@/typed/ModelTypes";

/**
 * Constructs a function to handle the selection of a range of rows in a table,
 * based on a previously selected row and the current clicked row. This is useful
 * for implementing shift-click selection behavior in a table UI.
 *
 * If no previous selection exists (or the same row is selected again), the function
 * selects only the current row. If a previous selection exists, it selects all rows
 * between the last checked row and the currently clicked row (inclusive).
 *
 * Updates the Redux store with the selected rows and manages the details drawer state.
 *
 * @template T - The type of items in the data array.
 *
 * @param {Object} params - Configuration object.
 * @param {string} params.stateName - The name of the state slice in the Redux store.
 * @param {string} params.page - The page name for context in the details drawer.
 * @param {string | undefined} params.tab - The tab name for context in the details drawer.
 * @param {(entry: T) => string} params.mapId - A function that maps a data entry to its unique string ID.
 * @param {T[]} params.data - The full array of data items to be considered for selection.
 *
 * @returns {(id: string, itemType: ModelType) => void} - A function that, when given an ID and item type,
 * performs row selection logic and updates the Redux store accordingly.
 */

export const constructSelectRangeOfRows =
  <T,>({
    stateName,
    page,
    tab,
    mapId,
    data
  }: {
    stateName: string;
    page: string;
    tab: string | undefined;
    mapId: (entry: T) => string;
    data: T[];
  }) =>
  (id: string, itemType: ModelType) => {
    const state = store.getState();
    const lastChecked = state.tables.data[stateName].lastChecked;

    if (lastChecked === undefined || lastChecked === id) {
      store.dispatch(
        setTableChecked({
          name: stateName,
          checked: { [id]: true },
          lastChecked: id
        })
      );
      store.dispatch(
        setDetailsItem({
          page: page,
          tab: tab,
          itemType: itemType,
          selectedId: id,
          itemLabel: "combined"
        })
      );
      return;
    }

    const ids = data.map(mapId);
    const [a, b] = [ids.indexOf(lastChecked), ids.indexOf(id)].sort((x, y) => x - y);
    const toCheck = Object.fromEntries(ids.slice(a, b + 1).map((selectedId) => [selectedId, true]));

    const selectedIds = Object.keys(toCheck).filter((id) => toCheck[id]);
    if (selectedIds.length === 1) {
      store.dispatch(
        setDetailsItem({
          page: page,
          tab: tab,
          itemType: itemType,
          itemLabel: "combined",
          selectedId: selectedIds[0]
        })
      );
    } else {
      store.dispatch(
        setSelectedIds({
          page: page,
          tab: tab,
          selectedIds: selectedIds
        })
      );
    }

    store.dispatch(
      setTableChecked({
        name: stateName,
        checked: toCheck
      })
    );
  };
